import { createApi } from "@reduxjs/toolkit/query/react";
import { customFetchBaseQuery } from "./customFetchBaseQuery";

// Types
export interface DocumentFolder {
  _id: string;
  userId: string;
  propertyId: string;
  folderName: string;
  fileCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentFile {
  _id: string;
  userId: string;
  propertyId: string;
  folderId: string | null;
  fileName: string;
  s3Key: string;
  fileType: string;
  fileSize: number;
  // October 2026 Documents tab. Files from before types existed read as "other".
  documentType?: string;
  createdAt: string;
  updatedAt: string;
}

export interface StorageUsage {
  usedBytes: number;
  totalBytes: number;
  usedMB: string;
  totalMB: string;
  // Per-file upload cap (bytes).
  maxFileBytes?: number;
}

export interface DocumentsResponse {
  success: boolean;
  data: {
    folders: DocumentFolder[];
    files: DocumentFile[];
    rootFileCount: number;
  };
}

export interface StorageUsageResponse {
  success: boolean;
  data: StorageUsage;
}

export interface CreateFolderBody {
  propertyId: string;
  folderName: string;
}

export interface UploadFileBody {
  propertyId: string;
  folderId?: string | null;
  file: File;
}

export const apiDocumentsSlice = createApi({
  reducerPath: "api-documents",
  baseQuery: customFetchBaseQuery,
  tagTypes: ["Documents", "Storage", "FolderFiles"],
  keepUnusedDataFor: 300,
  endpoints: (builder) => ({
    // Get all documents (folders and files) for a property
    getDocuments: builder.query<DocumentsResponse, string>({
      query: (propertyId) => `/api/documents/${propertyId}`,
      providesTags: ["Documents"],
    }),

    // Get user's storage usage
    getStorageUsage: builder.query<StorageUsageResponse, void>({
      query: () => `/api/documents/storage/usage`,
      providesTags: ["Storage"],
    }),

    // Get files in a specific folder
    getFilesInFolder: builder.query<{ success: boolean; data: DocumentFile[] }, { folderId: string; propertyId: string }>({
      query: ({ folderId, propertyId }) => `/api/documents/folder/${folderId}/files?propertyId=${propertyId}`,
      providesTags: (_result, _error, { folderId }) => [{ type: "FolderFiles", id: folderId }],
    }),

    // Create a new folder
    createFolder: builder.mutation<{ success: boolean; data: DocumentFolder; message: string }, CreateFolderBody>({
      query: (body) => ({
        url: `/api/documents/folder`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Documents"],
    }),

    // Delete a folder
    deleteFolder: builder.mutation<{ success: boolean; message: string; data: { deletedFiles: number; freedBytes: number } }, string>({
      query: (folderId) => ({
        url: `/api/documents/folder/${folderId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Documents", "Storage"],
    }),

    // Upload a file
    uploadFile: builder.mutation<{ success: boolean; data: DocumentFile; message: string }, FormData>({
      query: (formData) => ({
        url: `/api/documents/file`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Documents", "Storage"],
    }),

    // Delete a file
    deleteFile: builder.mutation<{ success: boolean; message: string; data: { freedBytes: number } }, string>({
      query: (fileId) => ({
        url: `/api/documents/file/${fileId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Documents", "Storage"],
    }),

    // Re-file a document under a different type
    updateFileType: builder.mutation<{ success: boolean; data: DocumentFile }, { fileId: string; documentType: string }>({
      query: ({ fileId, documentType }) => ({
        url: `/api/documents/file/${fileId}`,
        method: "PATCH",
        body: { documentType },
      }),
      invalidatesTags: ["Documents"],
    }),

    // Get download URL for a file
    getDownloadUrl: builder.query<{ success: boolean; data: { downloadUrl: string; fileName: string; fileType: string } }, string>({
      query: (fileId) => `/api/documents/file/${fileId}/download`,
      keepUnusedDataFor: 0, // Don't cache download URLs as they expire
    }),
  }),
});

export const {
  useGetDocumentsQuery,
  useLazyGetDocumentsQuery,
  useGetStorageUsageQuery,
  useLazyGetStorageUsageQuery,
  useGetFilesInFolderQuery,
  useLazyGetFilesInFolderQuery,
  useCreateFolderMutation,
  useDeleteFolderMutation,
  useUploadFileMutation,
  useDeleteFileMutation,
  useUpdateFileTypeMutation,
  useLazyGetDownloadUrlQuery,
} = apiDocumentsSlice;
